import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Users, TrendingUp, TrendingDown, Building2, Award, DollarSign, UserPlus,
  Calendar, AlertTriangle, Sparkles, GraduationCap, Briefcase, Activity,
  RefreshCw, Cake, Gift, FileWarning, ShieldCheck, BookOpen, Megaphone, Clock,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, FunnelChart, Funnel,
  RadialBarChart, RadialBar, LabelList, CartesianGrid,
} from 'recharts';
import { API_BASE as API } from '../lib/apiBase';

const auth = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Vibrant chart palette — each widget gets distinct hues for visual variety
const PAL = ['#6366F1', '#EC4899', '#F59E0B', '#10B981', '#EF4444', '#3B82F6', '#A855F7', '#14B8A6', '#F97316', '#84CC16'];
const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const fmtINR = (n) => '₹' + new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

function Sparkline({ data, color = '#6366F1' }) {
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => `${(i / Math.max(data.length - 1, 1)) * 100},${30 - (v / max) * 25}`).join(' ');
  return (
    <svg viewBox="0 0 100 32" width="100%" height="32" preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" />
    </svg>
  );
}

function KPI({ icon: Icon, label, value, sub, color = 'indigo', trend, testid }) {
  const C = {
    indigo: 'from-indigo-500 to-indigo-700 text-white',
    pink: 'from-pink-500 to-rose-700 text-white',
    amber: 'from-amber-500 to-orange-700 text-white',
    emerald: 'from-emerald-500 to-emerald-700 text-white',
    red: 'from-red-500 to-rose-700 text-white',
    sky: 'from-sky-500 to-blue-700 text-white',
    violet: 'from-violet-500 to-purple-700 text-white',
    teal: 'from-teal-500 to-cyan-700 text-white',
  }[color] || 'from-slate-600 to-slate-800 text-white';
  return (
    <Card data-testid={testid} className={`bg-gradient-to-br ${C} border-0 shadow-lg overflow-hidden relative`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <Icon className="h-7 w-7 opacity-80" />
          {trend !== undefined && trend !== null && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${trend >= 0 ? 'bg-white/20' : 'bg-black/20'}`}>
              {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {Math.abs(trend)}%
            </span>
          )}
        </div>
        <div className="text-3xl font-bold leading-tight">{value}</div>
        <div className="text-xs opacity-90 mt-1">{label}</div>
        {sub && <div className="text-[10px] opacity-75 mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function WidgetCard({ title, subtitle, children, className = '', icon: Icon, color = '#6366F1', testid }) {
  return (
    <Card data-testid={testid} className={`shadow-sm hover:shadow-md transition-shadow border-slate-200 dark:border-slate-800 ${className}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              {Icon && <Icon className="h-4 w-4" style={{ color }} />}
              {title}
            </div>
            {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

export default function ExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/api/dashboard/executive`, { headers: auth(), credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load analytics');
      setData(await res.json());
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-500"><RefreshCw className="animate-spin h-5 w-5 mr-2" />Loading executive dashboard…</div>;
  if (error) return <div className="text-red-600 p-6">{error}</div>;
  if (!data) return null;

  const hc = data.headcount, att = data.attendance, lv = data.leaves, pay = data.payroll;
  const rec = data.recruitment, perf = data.performance, comp = data.compliance, eng = data.engagement;

  return (
    <div className="space-y-6 p-2 md:p-4" data-testid="exec-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-indigo-600 via-pink-600 to-amber-500 bg-clip-text text-transparent">
            Executive Dashboard
          </h1>
          <p className="text-xs text-slate-500">
            {data.scope.is_cross_institute ? 'Across ALL institutes' : `Scoped to ${data.scope.total_entities} entit${data.scope.total_entities === 1 ? 'y' : 'ies'}`}
            {' · '}Last refreshed {new Date(data.generated_at).toLocaleTimeString()}
          </p>
        </div>
        <Button onClick={fetchAll} variant="outline" size="sm" data-testid="exec-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
      </div>

      {/* ============ ROW 1 — HERO KPIs ============ */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KPI testid="kpi-headcount" icon={Users} label="Total Headcount" value={fmt(hc.total)} color="indigo" trend={hc.delta_pct} sub={`${hc.new_this_month} new this month`} />
        <KPI testid="kpi-attendance" icon={Activity} label="Attendance Today" value={`${att.today_pct}%`} color="emerald" sub={`${fmt(att.today_present)} of ${fmt(hc.total)} present`} />
        <KPI testid="kpi-pending-leaves" icon={Calendar} label="Pending Approvals" value={fmt(lv.pending_approvals)} color="amber" sub="leave requests" />
        {pay.can_see && <KPI testid="kpi-payroll" icon={DollarSign} label="Monthly Payroll" value={fmtINR(pay.monthly_outflow)} color="pink" sub={`${pay.revisions_this_quarter} revisions Q`} />}
        <KPI testid="kpi-open-roles" icon={Briefcase} label="Open Positions" value={fmt(rec.open_positions)} color="sky" sub={`${rec.applications_this_month} apps this month`} trend={rec.applications_delta_pct} />
        <KPI testid="kpi-risk" icon={AlertTriangle} label="Risk Alerts" value={fmt(comp.orphan_users + comp.docs_expiring_30d)} color="red" sub={`${comp.orphan_users} orphans · ${comp.docs_expiring_30d} docs expiring`} />
      </div>

      {/* ============ ROW 2 — Headcount distribution ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <WidgetCard testid="w-by-institute" title="Headcount by Institute" subtitle="Active employees per entity" icon={Building2} color="#6366F1">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={hc.by_institute} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {hc.by_institute.map((_, i) => <Cell key={i} fill={PAL[i % PAL.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs mt-2">
            {hc.by_institute.slice(0, 6).map((d, i) => (
              <div key={d.name} className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: PAL[i % PAL.length] }}></span>{d.name}: <strong>{d.value}</strong></div>
            ))}
          </div>
        </WidgetCard>

        <WidgetCard testid="w-by-dept" title="Top Departments" subtitle="Active staff by department" icon={GraduationCap} color="#EC4899">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hc.by_department.slice(0, 8)} layout="vertical" margin={{ left: 30 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
              <Tooltip />
              <Bar dataKey="value" fill="#EC4899" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </WidgetCard>

        <WidgetCard testid="w-by-category" title="Teaching vs Non-Teaching" icon={Award} color="#F59E0B">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={hc.by_category} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={(e) => `${e.name} ${Math.round(e.value)}`}>
                {hc.by_category.map((_, i) => <Cell key={i} fill={PAL[(i + 2) % PAL.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </WidgetCard>

        <WidgetCard testid="w-by-wing" title="Wing Split (Diploma vs Degree)" icon={GraduationCap} color="#8B5CF6">
          {hc.by_wing.length === 0 ? (
            <div className="text-xs text-slate-400 h-[220px] flex items-center justify-center text-center px-4">No wing data yet — populate the <strong className="mx-1">wing</strong> field on employee records to enable this chart.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={hc.by_wing} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80}>
                  {hc.by_wing.map((_, i) => <Cell key={i} fill={PAL[(i + 5) % PAL.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </WidgetCard>

        <WidgetCard testid="w-by-appt" title="Appointment Types" subtitle="Permanent / Contract / Visiting / etc." icon={Briefcase} color="#10B981">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hc.by_appointment}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#10B981" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </WidgetCard>

        <WidgetCard testid="w-tenure" title="Tenure Distribution" subtitle="Years in organisation" icon={Clock} color="#3B82F6">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hc.tenure.map(t => ({ name: t.bucket + ' yr', value: t.value }))}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#3B82F6" radius={[8, 8, 0, 0]}>
                {hc.tenure.map((_, i) => <Cell key={i} fill={PAL[(i + 4) % PAL.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </WidgetCard>

        <WidgetCard testid="w-gender" title="Gender Ratio" icon={Users} color="#F97316">
          {hc.by_gender.length === 0 ? (
            <div className="text-xs text-slate-400 h-[220px] flex items-center justify-center text-center px-4">No gender data captured yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={hc.by_gender} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={(e) => `${e.name} ${e.value}`}>
                  {hc.by_gender.map((_, i) => <Cell key={i} fill={i === 0 ? '#3B82F6' : i === 1 ? '#EC4899' : '#A855F7'} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </WidgetCard>

        <WidgetCard testid="w-attrition" title="Joiners vs Exits — 12 Months" icon={TrendingUp} color="#EF4444" className="md:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={hc.trend_12m}>
              <defs>
                <linearGradient id="gJoin" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.7} /><stop offset="100%" stopColor="#10B981" stopOpacity={0.05} /></linearGradient>
                <linearGradient id="gExit" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#EF4444" stopOpacity={0.7} /><stop offset="100%" stopColor="#EF4444" stopOpacity={0.05} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Area type="monotone" dataKey="joiners" stroke="#10B981" fill="url(#gJoin)" name="Joiners" />
              <Area type="monotone" dataKey="exits" stroke="#EF4444" fill="url(#gExit)" name="Exits" />
            </AreaChart>
          </ResponsiveContainer>
        </WidgetCard>
      </div>

      {/* ============ ROW 3 — Attendance ============ */}
      <h2 className="text-lg font-semibold flex items-center gap-2 mt-4"><Activity className="h-5 w-5 text-emerald-600" /> Attendance Live</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <KPI testid="kpi-present" icon={Users} label="Present" value={fmt(att.today_present)} color="emerald" />
        <KPI testid="kpi-absent" icon={Users} label="Absent" value={fmt(att.today_absent)} color="red" />
        <KPI testid="kpi-on-leave" icon={Calendar} label="On Leave" value={fmt(att.today_on_leave)} color="amber" />
        <KPI testid="kpi-late" icon={Clock} label="Late Arrivals" value={fmt(att.today_late)} color="pink" />
        <KPI testid="kpi-geo" icon={AlertTriangle} label="Geofence Breach 24h" value={fmt(att.geofence_violations_24h)} color="violet" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <WidgetCard testid="w-att-trend" title="30-day Attendance Trend" icon={Activity} color="#10B981" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={att.trend_30d}>
              <defs><linearGradient id="gA" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.6} /><stop offset="100%" stopColor="#10B981" stopOpacity={0.02} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={3} />
              <YAxis tick={{ fontSize: 10 }} unit="%" domain={[0, 100]} />
              <Tooltip />
              <Area type="monotone" dataKey="percent" stroke="#10B981" fill="url(#gA)" />
            </AreaChart>
          </ResponsiveContainer>
        </WidgetCard>
        <WidgetCard testid="w-punctual" title="Most Punctual Departments" subtitle="Today" icon={Award} color="#F59E0B">
          {att.punctual_top5.length === 0 ? <div className="text-xs text-slate-400 py-8 text-center">No check-ins logged today.</div> : (
            <div className="space-y-2 mt-2">
              {att.punctual_top5.map((p, i) => (
                <div key={p.department} className="flex items-center gap-2 text-sm">
                  <span className="w-6 text-center font-bold text-amber-500">#{i + 1}</span>
                  <span className="flex-1 truncate">{p.department}</span>
                  <span className="font-mono text-xs">{p.punctual_pct}%</span>
                  <div className="w-16 bg-slate-100 dark:bg-slate-800 rounded-full h-1.5"><div className="bg-amber-500 h-1.5 rounded-full" style={{ width: `${p.punctual_pct}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </WidgetCard>
        <WidgetCard testid="w-att-per-inst" title="Attendance % by Institute" subtitle="Today" icon={Building2} color="#3B82F6" className="lg:col-span-3">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={att.per_institute}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="institute" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} unit="%" domain={[0, 100]} />
              <Tooltip />
              <Bar dataKey="percent" radius={[8, 8, 0, 0]}>
                {att.per_institute.map((_, i) => <Cell key={i} fill={PAL[i % PAL.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </WidgetCard>
      </div>

      {/* ============ ROW 4 — Leaves ============ */}
      <h2 className="text-lg font-semibold flex items-center gap-2 mt-4"><Calendar className="h-5 w-5 text-violet-600" /> Leaves</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <WidgetCard testid="w-leave-types" title="Leave Types — last 6 months" icon={Calendar} color="#A855F7">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={lv.types_breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name} ${e.value}`}>
                {lv.types_breakdown.map((_, i) => <Cell key={i} fill={PAL[(i + 3) % PAL.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </WidgetCard>
        <WidgetCard testid="w-leave-trend" title="Consumption Trend — 6 mo" icon={TrendingUp} color="#6366F1" className="md:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={lv.consumption_trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              {Object.keys(lv.consumption_trend[0] || {}).filter(k => k !== 'month').map((k, i) => (
                <Bar key={k} dataKey={k} stackId="a" fill={PAL[i % PAL.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </WidgetCard>
        <WidgetCard testid="w-top-takers" title="Top 5 Leave Takers" subtitle="Last 6 months" icon={Users} color="#EC4899">
          {lv.top_5_takers.length === 0 ? <div className="text-xs text-slate-400 py-6 text-center">No data.</div> : (
            <div className="space-y-2 mt-2">
              {lv.top_5_takers.map((t, i) => (
                <div key={t.employee_id} className="flex items-center gap-2 text-sm">
                  <span className="w-7 h-7 rounded-full bg-pink-100 dark:bg-pink-950 text-pink-700 text-xs font-bold flex items-center justify-center">#{i + 1}</span>
                  <div className="flex-1 truncate">
                    <div className="truncate text-sm">{t.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{t.department}</div>
                  </div>
                  <Badge className="bg-pink-100 text-pink-800">{t.days}d</Badge>
                </div>
              ))}
            </div>
          )}
        </WidgetCard>
        <WidgetCard testid="w-leave-approve" title="Avg Approval Time" icon={Clock} color="#F59E0B">
          <div className="flex flex-col items-center justify-center py-6">
            <div className="text-5xl font-bold text-amber-600">{lv.avg_approval_days}</div>
            <div className="text-sm text-slate-500 mt-1">days on average</div>
          </div>
        </WidgetCard>
      </div>

      {/* ============ ROW 5 — Payroll ============ */}
      {pay.can_see && (
        <>
          <h2 className="text-lg font-semibold flex items-center gap-2 mt-4"><DollarSign className="h-5 w-5 text-pink-600" /> Payroll & Finance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <WidgetCard testid="w-pay-by-inst" title="Payroll by Institute" subtitle="Monthly outflow" icon={Building2} color="#EC4899" className="md:col-span-2">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={pay.by_institute}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="institute" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => '₹' + (v >= 100000 ? (v / 100000).toFixed(1) + 'L' : (v / 1000).toFixed(0) + 'k')} />
                  <Tooltip formatter={(v) => fmtINR(v)} />
                  <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
                    {pay.by_institute.map((_, i) => <Cell key={i} fill={PAL[i % PAL.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </WidgetCard>
            <WidgetCard testid="w-pay-bands" title="Salary Band Distribution" icon={DollarSign} color="#F97316">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={pay.salary_bands}>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#F97316" radius={[8, 8, 0, 0]}>
                    {pay.salary_bands.map((_, i) => <Cell key={i} fill={PAL[(i + 6) % PAL.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </WidgetCard>
            {!pay.top_10_paid_restricted && (
              <WidgetCard testid="w-top-paid" title="Top 10 Paid (admin-only)" icon={Award} color="#F59E0B" className="md:col-span-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {pay.top_10_paid.map((t, i) => (
                    <div key={i} className="flex items-center justify-between border-l-4 py-1.5 px-3 rounded-md hover:bg-slate-50 dark:hover:bg-slate-900" style={{ borderColor: PAL[i % PAL.length] }}>
                      <div>
                        <div className="text-sm font-medium truncate">{t.name}</div>
                        <div className="text-[10px] text-slate-500 truncate">{t.designation} · {t.department}</div>
                      </div>
                      <span className="text-sm font-mono font-bold text-amber-600">{fmtINR(t.salary)}</span>
                    </div>
                  ))}
                </div>
              </WidgetCard>
            )}
          </div>
        </>
      )}

      {/* ============ ROW 6 — Recruitment ============ */}
      <h2 className="text-lg font-semibold flex items-center gap-2 mt-4"><Briefcase className="h-5 w-5 text-sky-600" /> Recruitment</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <WidgetCard testid="w-funnel" title="Hiring Funnel" subtitle="All time" icon={Briefcase} color="#3B82F6">
          <div className="space-y-2 mt-2">
            {rec.funnel.map((f, i) => {
              const max = rec.funnel[0]?.value || 1;
              const pct = (f.value / max) * 100;
              return (
                <div key={f.name}>
                  <div className="flex items-center justify-between text-sm">
                    <span>{f.name}</span><span className="font-bold">{f.value}</span>
                  </div>
                  <div className="h-3 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: PAL[i % PAL.length] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </WidgetCard>
        <WidgetCard testid="w-open-by-dept" title="Open Roles by Department" icon={Users} color="#10B981" className="md:col-span-2">
          {rec.open_by_department.length === 0 ? <div className="text-xs text-slate-400 py-10 text-center">No open positions.</div> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={rec.open_by_department}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#10B981" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </WidgetCard>
        <WidgetCard testid="w-pipeline-wing" title="Pipeline by Wing" icon={GraduationCap} color="#A855F7">
          {rec.pipeline_by_wing.length === 0 ? <div className="text-xs text-slate-400 py-10 text-center">No wing-tagged jobs.</div> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={rec.pipeline_by_wing} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={(e) => `${e.name} ${e.value}`}>
                  {rec.pipeline_by_wing.map((_, i) => <Cell key={i} fill={PAL[(i + 7) % PAL.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </WidgetCard>
        <WidgetCard testid="w-tth" title="Avg Time to Hire" icon={Clock} color="#06B6D4">
          <div className="flex flex-col items-center justify-center py-6">
            <div className="text-5xl font-bold text-cyan-600">{rec.time_to_hire_days}</div>
            <div className="text-sm text-slate-500 mt-1">days</div>
          </div>
        </WidgetCard>
        <WidgetCard testid="w-hire-trend" title="Applications Trend — 6 mo" icon={TrendingUp} color="#6366F1">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={rec.trend_6m}>
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="applications" stroke="#6366F1" strokeWidth={2.5} dot={{ fill: '#6366F1', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </WidgetCard>
      </div>

      {/* ============ ROW 7 — Performance ============ */}
      <h2 className="text-lg font-semibold flex items-center gap-2 mt-4"><Award className="h-5 w-5 text-amber-600" /> Performance & L&D</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <WidgetCard testid="w-reviews" title="Reviews Completed vs Pending" icon={Award} color="#F59E0B">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={[{ name: 'Completed', value: perf.reviews_completed }, { name: 'Pending', value: perf.reviews_pending }]} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80}>
                <Cell fill="#10B981" /><Cell fill="#F59E0B" />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </WidgetCard>
        <WidgetCard testid="w-perf-by-dept" title="Avg Performance Score" subtitle="By department" icon={TrendingUp} color="#10B981" className="md:col-span-2">
          {perf.avg_score_by_department.length === 0 ? <div className="text-xs text-slate-400 py-10 text-center">No reviews captured.</div> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={perf.avg_score_by_department}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 10 }} domain={[0, 5]} />
                <Tooltip />
                <Bar dataKey="avg_score" fill="#10B981" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </WidgetCard>
        <WidgetCard testid="w-top-perf" title="Top 5 Performers" icon={Sparkles} color="#EC4899">
          {perf.top_performers.length === 0 ? <div className="text-xs text-slate-400 py-6 text-center">No data.</div> : (
            <div className="space-y-2 mt-2">
              {perf.top_performers.map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-950 text-amber-700 text-xs font-bold flex items-center justify-center">#{i + 1}</span>
                  <div className="flex-1 truncate"><div className="truncate">{t.name}</div><div className="text-[10px] text-slate-500 truncate">{t.department}</div></div>
                  <Badge className="bg-amber-100 text-amber-800">{t.score}/5</Badge>
                </div>
              ))}
            </div>
          )}
        </WidgetCard>
        <WidgetCard testid="w-trainings" title="Trainings Completed" icon={BookOpen} color="#8B5CF6">
          <div className="flex flex-col items-center justify-center py-6">
            <div className="text-5xl font-bold text-violet-600">{fmt(perf.trainings_completed)}</div>
            <div className="text-sm text-slate-500 mt-1">this year</div>
          </div>
        </WidgetCard>
      </div>

      {/* ============ ROW 8 — Compliance ============ */}
      <h2 className="text-lg font-semibold flex items-center gap-2 mt-4"><ShieldCheck className="h-5 w-5 text-red-600" /> Compliance & Risk</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KPI testid="kpi-docs-exp" icon={FileWarning} label="Docs Expiring 30d" value={fmt(comp.docs_expiring_30d)} color="amber" />
        <KPI testid="kpi-orphans" icon={Users} label="Orphan Logins" value={fmt(comp.orphan_users)} color="red" />
        <KPI testid="kpi-inactive" icon={Users} label="Inactive Accounts" value={fmt(comp.inactive_accounts)} color="violet" />
        <KPI testid="kpi-audit" icon={Activity} label="Audit Events 24h" value={fmt(comp.audit_24h_count)} color="sky" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <WidgetCard testid="w-audit-spark" title="Audit Activity — last 24h" subtitle="Events per hour" icon={Activity} color="#3B82F6">
          <div className="py-2">
            <Sparkline data={comp.audit_24h_spark} color="#3B82F6" />
          </div>
          <div className="text-xs text-slate-500 mt-1">Total: {fmt(comp.audit_24h_count)} events</div>
        </WidgetCard>
        <WidgetCard testid="w-compliance-status" title="Compliance Status" subtitle="Per institute" icon={ShieldCheck} color="#10B981" className="md:col-span-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
            {comp.status_by_institute.slice(0, 12).map((s, i) => (
              <div key={i} className="flex items-center gap-2 border rounded-md p-2 text-sm">
                <div className={`w-2 h-2 rounded-full ${s.is_active ? 'bg-green-500' : 'bg-slate-300'}`} />
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{s.institute}</div>
                  <div className="text-[10px] text-slate-500 truncate">{s.compliance_body || '—'}</div>
                </div>
              </div>
            ))}
          </div>
        </WidgetCard>
      </div>

      {/* ============ ROW 9 — Engagement ============ */}
      <h2 className="text-lg font-semibold flex items-center gap-2 mt-4"><Cake className="h-5 w-5 text-pink-600" /> Engagement & Culture</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <WidgetCard testid="w-birthdays" title="Birthdays This Week" icon={Cake} color="#EC4899">
          {eng.birthdays_this_week.length === 0 ? <div className="text-xs text-slate-400 py-6 text-center">No birthdays this week.</div> : (
            <div className="space-y-2 mt-2 max-h-[200px] overflow-y-auto">
              {eng.birthdays_this_week.map((b, i) => (
                <div key={i} className="flex items-center gap-2 border-l-4 border-pink-400 pl-2 py-1">
                  <Cake className="w-4 h-4 text-pink-500" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm">{b.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{b.department}</div>
                  </div>
                  <Badge className="bg-pink-100 text-pink-700">{b.date}</Badge>
                </div>
              ))}
            </div>
          )}
        </WidgetCard>
        <WidgetCard testid="w-annivs" title="Work Anniversaries This Month" icon={Gift} color="#A855F7">
          {eng.anniversaries_this_month.length === 0 ? <div className="text-xs text-slate-400 py-6 text-center">No anniversaries this month.</div> : (
            <div className="space-y-2 mt-2 max-h-[200px] overflow-y-auto">
              {eng.anniversaries_this_month.map((a, i) => (
                <div key={i} className="flex items-center gap-2 border-l-4 border-violet-400 pl-2 py-1">
                  <Gift className="w-4 h-4 text-violet-500" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm">{a.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{a.department}</div>
                  </div>
                  <Badge className="bg-violet-100 text-violet-700">{a.years} yr · {a.date}</Badge>
                </div>
              ))}
            </div>
          )}
        </WidgetCard>
        <WidgetCard testid="w-notices" title="Active Notices" icon={Megaphone} color="#F59E0B">
          <div className="flex flex-col items-center justify-center py-6">
            <Megaphone className="w-12 h-12 text-amber-500 mb-2" />
            <div className="text-4xl font-bold text-amber-600">{fmt(eng.active_notices)}</div>
            <div className="text-sm text-slate-500 mt-1">notices live</div>
          </div>
        </WidgetCard>
      </div>

      <div className="text-center text-xs text-slate-400 pt-4 pb-2">
        51 widgets · auto-refresh on demand · scoped to your access level
      </div>
    </div>
  );
}
